import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { getInnerRouterPath } from '@app/app.reducer';
import { getSortedTeamMembersByLastModifiedDate } from '@collections/jobs';
import { getUsersLoggedIn, getUsersEntities } from '@collections/users';
import { loadLicenseExtensions } from '@core/settings/license-configuration';
import {
  getNotesFiltersFromLocalStorage,
  setActiveNotesFiltersId,
  upsertNotesFilters,
} from '@modules/notes/filters-store/notes-filters.actions';
import { selectActiveNotesFilters } from '@modules/notes/filters-store/notes-filters.selectors';
import {
  NotesFiltersInterface,
  NotesFiltersService,
} from '@modules/notes/filters-store/notes-filters.service';
import { notesOpenModal } from '@modules/notes/store';
import { Store } from '@ngrx/store';
import { isTruthy, User } from '@shared';
import { TeamMembersListItem } from '@shared/models/team-members-list-item.model';
import { TeamRole } from '@shared/models/team-role.enum';
import { RouteParamsService } from '@shared/services/route-params.service';
import { getNoteStateLoading, notesObj, NoteTypes, NoteSubject } from 'mojo-irp-notes';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Dictionary } from '@ngrx/entity';
import { User as NotesUser } from 'mojo-irp-notes/lib/models/user';

@Component({
  selector: 'mojo-notes-container',
  templateUrl: './notes.container.html',
  styleUrls: ['./notes.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesContainerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isWidgetView = false;
  @Input() pagination = true;
  @Input() subject: NoteSubject;
  @Input() secondarySubject: NoteSubject;
  @Input() showHiringManagerTab = true;
  @Input() showRecruiterTab = true;
  @Input() setDefaultNotesPageFilter = false;
  @Input() subjectId: number;
  @Input() sideDrawerConfiguration = false;
  @Input() closeNotes = new BehaviorSubject(false);

  @Input() isExpandible = false;
  isPanelOpen = true;

  readonly singleTabNotes = AppFeatureExtensions.NOTES_SINGLE_TAB;

  private activeNotesFilters: NotesFiltersInterface;
  isSingleTabsNotesFeatureExtensionEnabled: boolean;
  private loggedInUser: User;
  private subscription: Subscription = new Subscription();
  private teamMembersList: TeamMembersListItem[];
  isNotesPage = false;
  private isHiringTeamNotesTabActive$ = new BehaviorSubject(false);

  currentRoute$ = this.store.select(getInnerRouterPath);

  isSearchInputVisible = false;

  originalSubjectType;

  notes$ = this.getNotesObservable();

  notesHiringManager$ = this.store.select(getNoteStateLoading).pipe(
    filter((value: any) => !value),
    switchMap(() => this.store.select(getUsersEntities)),
    switchMap((users) =>
      this.store.select(notesObj.getHiringManagerNotesList(users as Dictionary<NotesUser>)),
    ),
  );

  constructor(
    private notesFiltersService: NotesFiltersService,
    private store: Store<{}>,
    private route: ActivatedRoute,
    private routeParamsService: RouteParamsService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      !!this.loggedInUser &&
      changes.subjectId?.currentValue !== changes.subjectId?.previousValue
    ) {
      this.notes$ = undefined;
      this.subscribeToContext();
      this.notes$ = this.getNotesObservable();
    }
  }

  private getNotesObservable() {
    return this.store.select(getNoteStateLoading).pipe(
      filter((value: any) => !value),
      switchMap(() => this.store.select(getUsersEntities)),
      switchMap((users) =>
        this.store.select(notesObj.getNotesList(users as Dictionary<NotesUser>)),
      ),
    );
  }

  get loggedInUserIsHiringManager() {
    return this.loggedInUser.role.name.toLowerCase() === TeamRole.hiringManager.toLowerCase();
  }

  get numberOfActiveFilters() {
    if (this.shouldSetAllNotesAsChecked()) {
      return '2';
    }
    return '1';
  }

  get showAnonymizationWarning() {
    return !this.loggedInUserIsHiringManager && this.anyOfTeamMembersIsHiringManagerAndIsAnonymized;
  }

  get anyOfTeamMembersIsHiringManagerAndIsAnonymized() {
    return this.teamMembersList.some(
      (teamMember) =>
        teamMember.role === TeamRole.hiringManager && teamMember.user?.anonymization === true,
    );
  }

  ngOnInit() {
    this.originalSubjectType = this.subject;
    this.subscribeToActiveNotesFilters();
    this.subscribeToLoggedInUserAndTeamMembersList();
    this.subscribeToContext();

    this.selectedTabChange({ index: 0 });

    const hasDefaultFilter = !!this.setDefaultNotesPageFilter;
    const isJobSubject =
      this.subject === NoteSubject.Job || this.subject === NoteSubject.JobHiringManager;

    if (!hasDefaultFilter || isJobSubject) {
      this.subscribeToFeatureExtensions();
    } else {
      this.isSingleTabsNotesFeatureExtensionEnabled = true;
    }

    if (hasDefaultFilter) {
      this.setNotesFilters(true, true, false);
    }
    this.setupNotesForHiringManager();

    this.currentRoute$.subscribe((currentPage) => {
      this.isNotesPage = currentPage === 'notes';
    });
  }

  private setupNotesForHiringManager() {
    if (!this.isSingleTabsNotesFeatureExtensionEnabled && this.loggedInUserIsHiringManager) {
      this.store.dispatch(
        upsertNotesFilters({
          notesFilters: {
            isForHiringTeamTab: true,
          },
        }),
      );
    }
  }

  private subscribeToContext() {
    this.subscription.add(
      combineLatest([this.isHiringTeamNotesTabActive$])
        .pipe(
          switchMap(([isHiringManagerTabActive]) => {
            this.setSubjectForTabs(isHiringManagerTabActive);
            return this.updateFilter(this.subjectId, isHiringManagerTabActive);
          }),
        )
        .subscribe(),
    );
  }

  private setSubjectForTabs(isHiringManagerTabActive) {
    if (!!isHiringManagerTabActive) {
      this.subject =
        this.secondarySubject === NoteSubject.Candidate
          ? NoteSubject.CandidateHiringManager
          : NoteSubject.JobHiringManager;
    } else {
      this.subject = this.originalSubjectType;
    }
  }

  private subscribeToActiveNotesFilters() {
    this.subscription.add(
      this.store
        .select(selectActiveNotesFilters)
        .pipe(filter(isTruthy))
        .subscribe((notesFilters) => {
          this.activeNotesFilters = notesFilters;
          this.isSearchInputVisible = this.activeNotesFilters?.searchPhrases?.length > 0;
        }),
    );
  }

  private subscribeToFeatureExtensions() {
    this.subscription.add(
      this.store.select(loadLicenseExtensions).subscribe((licenseExtensions) => {
        this.isSingleTabsNotesFeatureExtensionEnabled = licenseExtensions?.includes(
          AppFeatureExtensions.NOTES_SINGLE_TAB,
        );
      }),
    );
  }

  private subscribeToLoggedInUserAndTeamMembersList() {
    this.subscription.add(
      this.store
        .select(getSortedTeamMembersByLastModifiedDate)
        .pipe(filter(isTruthy), withLatestFrom(this.store.select(getUsersLoggedIn)))
        .subscribe(([teamMembersList, loggedInUser]) => {
          this.teamMembersList = teamMembersList;
          this.loggedInUser = loggedInUser;
        }),
    );
  }

  setNotesFilters(userGenerated: boolean, systemGenerated: boolean, fromClick = true) {
    if (fromClick) {
      this.routeParamsService.updateRouteParams(
        this.generateProperNotesTypeQueryParam(userGenerated, systemGenerated),
        this.route,
      );
    }
    this.store.dispatch(
      upsertNotesFilters({
        notesFilters: {
          ...this.activeNotesFilters,
          pageNumber: 0,
          userGenerated,
          systemGenerated,
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  selectedTabChange($event: { index: number }) {
    this.isHiringTeamNotesTabActive$.next($event.index !== 0 || this.loggedInUserIsHiringManager);
  }

  private updateFilter(subjectId: number, isHiringTeamNotesTabActive: boolean) {
    return this.notesFiltersService
      .buildNotesFilters(
        subjectId,
        this.loggedInUser.id,
        true,
        true,
        isHiringTeamNotesTabActive,
        !isHiringTeamNotesTabActive,
        this.subject,
      )
      .pipe(
        tap((defaultNoteFilter) => {
          this.store.dispatch(setActiveNotesFiltersId({ notesFiltersId: defaultNoteFilter.id }));
          this.store.dispatch(
            getNotesFiltersFromLocalStorage({
              defaultNoteFilter,
              route: this.route,
              isWidgetView: this.isWidgetView,
            }),
          );
        }),
      );
  }

  toggleSearchInputVisibility(e) {
    e.stopPropagation();
    this.isSearchInputVisible = !this.isSearchInputVisible;
  }

  shouldSetUserNotesAsChecked() {
    return this.activeNotesFilters?.userGenerated && !this.activeNotesFilters?.systemGenerated;
  }

  shouldSetSystemNotesAsChecked() {
    return !this.shouldSetAllNotesAsChecked() && !this.shouldSetUserNotesAsChecked();
  }

  shouldSetAllNotesAsChecked() {
    const { userGenerated = false, systemGenerated = false } = this.activeNotesFilters || {};
    return (userGenerated && systemGenerated) || (!userGenerated && !systemGenerated);
  }

  generateProperNotesTypeQueryParam(userGenerated: boolean, systemGenerated: boolean) {
    let queryParamsObj = { notesType: NoteTypes.ALL_NOTES };
    if (!systemGenerated) {
      queryParamsObj = { ...queryParamsObj, notesType: NoteTypes.USER_GENERATED };
    } else if (!userGenerated) {
      queryParamsObj = { ...queryParamsObj, notesType: NoteTypes.SYSTEM_GENERATED };
    }
    return queryParamsObj;
  }

  openNoteModal(e) {
    e.stopPropagation();
    this.closeNotes.next(false);
    const data = {
      author: `${this.loggedInUser?.firstName} ${this.loggedInUser?.lastName}`,
      authorId: this.loggedInUser?.id,
      subjectId: this.subjectId,
      subject: this.subject,
      isEditMode: false,
      closeNotes: this.closeNotes,
    };
    this.store.dispatch(notesOpenModal({ data }));
  }

  expandedData() {
    if (!this.isNotesPage) this.isPanelOpen = !this.isPanelOpen;
  }
}
